'use client';

import { tv } from 'tailwind-variants';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { createContext, useContext, HTMLAttributes, ReactNode, useState, forwardRef, useEffect } from 'react';

interface EntityTabTriggerProps extends HTMLAttributes<HTMLButtonElement> {
  value: string;
}

const EntityTabContext = createContext<{
  activeTab: string;
  setActiveTab: (tab: string) => void;
} | null>(null);

export const useTabs = () => {
  const context = useContext(EntityTabContext);
  if (!context) {
    throw new Error('useTabs must be used within a TabsProvider');
  }
  return context;
};

const EntityTabProvider = ({ children }: { children: ReactNode }) => {
  const [activeTab, setActiveTab] = useState<string>('');

  return <EntityTabContext.Provider value={{ activeTab, setActiveTab }}>{children}</EntityTabContext.Provider>;
};

const entityTabListStyles = tv({
  slots: {
    container: ['w-full z-10'],
  },
});

const EntityTabList = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ children, className, ...props }, ref) => {
    const { container } = entityTabListStyles();

    return (
      <div className={container({ className })} {...props} ref={ref}>
        <div className="w-full flex items-center gap-5 justify-between">{children}</div>

        <div className="bg-gray-200 rounded w-full h-0.5" />
      </div>
    );
  },
);

EntityTabList.displayName = 'EntityTabList';

const entityTabTrigger = tv({
  slots: {
    container: ['w-full flex flex-col items-center relative cursor-pointer pb-1'],
    bar: ['w-0 opacity-0 h-0.5 bg-gray-900 rounded', 'absolute -bottom-0.5 transition-all duration-300'],
    buttonTrigger: ['text-gray-700 w-full text-base text-center'],
  },

  variants: {
    active: {
      true: {
        buttonTrigger: ['text-gray-900 font-bold w-full'],
        bar: ['w-full opacity-100'],
      },
    },
  },
});

const EntityTabTrigger = forwardRef<HTMLButtonElement, EntityTabTriggerProps>(
  ({ value, children, className, ...props }, ref) => {
    const { bar, container, buttonTrigger } = entityTabTrigger();
    const { activeTab, setActiveTab } = useTabs();

    const isActive = activeTab === value;

    const goToTab = () => {
      setActiveTab(value);

      const element = document.querySelector(`#${value}`) as HTMLElement;
      const offset = 80;

      if (!element) {
        return;
      }

      window.scrollTo({
        top: element.offsetTop - offset,
        behavior: 'smooth',
      });
    };

    return (
      <button
        className={container({ className })}
        {...props}
        ref={ref}
        data-testid={`${DataTestIdEnum.ENTITY_TAB_TRIGGER}-${value}`}
        onClick={(event) => {
          if (props.onClick) {
            props.onClick(event);
            return;
          }

          goToTab();
        }}
      >
        <span className={buttonTrigger({ active: isActive })}>{children}</span>

        <div className={bar({ active: isActive })} />
      </button>
    );
  },
);

EntityTabTrigger.displayName = 'EntityTabTrigger';

const entityTabContentStyle = tv({
  slots: {
    container: ['w-full'],
  },
});

interface EntityTabContentProps extends HTMLAttributes<HTMLDivElement> {
  value: string;
}

const EntityTabContent = forwardRef<HTMLDivElement, EntityTabContentProps>(
  ({ children, value, className, ...props }, ref) => {
    const { container } = entityTabContentStyle();

    return (
      <div
        id={value}
        className={container({ className })}
        {...props}
        ref={ref}
        data-testid={DataTestIdEnum.ENTITY_TAB_CONTENT}
      >
        {children}
      </div>
    );
  },
);

EntityTabContent.displayName = 'EntityTabContent';

interface EntityTabProps extends HTMLAttributes<HTMLDivElement> {
  defaultValue?: string;
  tabs?: string[];
}

const entityTabStyles = tv({
  slots: {
    container: ['w-full'],
  },
});

const EntityTab = forwardRef<HTMLDivElement, EntityTabProps>(({ children, className, tabs, ...props }, ref) => {
  const { container } = entityTabStyles();
  const { setActiveTab } = useTabs();

  useEffect(() => {
    if (props.defaultValue) {
      setActiveTab(props.defaultValue);
    }
  }, [props.defaultValue]);

  useEffect(() => {
    document.addEventListener('scroll', () => {
      tabs?.forEach((tab) => {
        const element = document.getElementById(tab);
        if (!element) {
          return;
        }

        const rect = element.getBoundingClientRect();
        if (rect.top <= 120 && rect.bottom >= 120) {
          setActiveTab(tab);
        }
      });
    });
  }, []);

  return (
    <div className={container({ className })} {...props} ref={ref} data-testid={DataTestIdEnum.ENTITY_TAB_CONTAINER}>
      {children}
    </div>
  );
});

EntityTab.displayName = 'EntityTab';

export { EntityTabProvider, EntityTabList, EntityTabContent, EntityTab, EntityTabTrigger };
